import React, { createRef, forwardRef, isValidElement, useRef } from 'react';

import { ToastContext } from '../Toast/ToastContext';
import { ToastContainerProps } from './ToastContainer.types';
import { classes } from './ToastContainer.classes';
import { useStackShift } from './hooks/useStackShift';
import { useExitTransition } from './hooks/useExitTransition';

/* mirrors $duration / $shift-easing in styles/ToastContainer.module.scss */
const SHIFT_DURATION = 300;
const SHIFT_EASING = 'cubic-bezier(0, 0, 0.2, 1)';

let reducedMotionQuery: MediaQueryList | null | undefined;

const prefersReducedMotion = () => {
  if (reducedMotionQuery === undefined) {
    reducedMotionQuery =
      typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia('(prefers-reduced-motion: reduce)')
        : null;
  }

  return !!reducedMotionQuery?.matches;
};

const ToastContainer = forwardRef<HTMLDivElement, ToastContainerProps>(
  (
    { offset, zIndex, dataHook, position, maxToasts = 1, className, children },
    ref,
  ) => {
    const toasts = React.Children.toArray(children)
      .filter(isValidElement)
      .reverse() as React.ReactElement[];
    const stacked = maxToasts > 1;
    const reducedMotion = prefersReducedMotion();

    const itemRefs = useRef(new Map<string, React.RefObject<HTMLDivElement>>());
    const hiddenByKey = useRef(new Map<string, boolean>());

    const getItemRef = (key: string) => {
      let itemRef = itemRefs.current.get(key);

      if (!itemRef) {
        itemRef = createRef<HTMLDivElement>();
        itemRefs.current.set(key, itemRef);
      }

      return itemRef;
    };

    const entries = useExitTransition({
      elements: toasts,
      getNode: key => getItemRef(key).current,
    });

    const itemKeys = entries.map(entry => entry.key).join('|');

    useStackShift({
      itemKeys,
      itemRefs,
      enabled: stacked && !reducedMotion,
      duration: SHIFT_DURATION,
      easing: SHIFT_EASING,
    });

    const nextHidden = new Map<string, boolean>();
    let liveIndex = 0;

    const items = entries.map(({ key, element, exiting }) => {
      let hidden: boolean;

      if (exiting) {
        hidden = hiddenByKey.current.get(key) ?? false;
      } else {
        hidden = liveIndex >= maxToasts;
        liveIndex += 1;
      }

      nextHidden.set(key, hidden);

      const containerKey = `toast-container-${key}`;

      return (
        <div
          key={containerKey}
          ref={getItemRef(key)}
          data-hook={containerKey}
          data-hidden={hidden}
          className={classes.item({ hidden, exiting })}
        >
          <ToastContext.Provider value={{ hidden }}>
            {element}
          </ToastContext.Provider>
        </div>
      );
    });

    hiddenByKey.current = nextHidden;

    return (
      <div
        ref={ref}
        className={classes.root({ stacked }, className)}
        data-hook={dataHook}
        style={{ bottom: offset, zIndex, position: position || 'fixed' }}
      >
        {items}
      </div>
    );
  },
);

ToastContainer.displayName = 'ToastContainer';

export { ToastContainer };
